import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	List,
	ListItemButton,
	ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { PassengerDocumentEnum, passengerDocumentType } from 'features/salesOrder/types';
import { PassengerSearchMatch, usePassengerByDocument } from '../../hooks/usePassengerByDocument';

interface Props {
	onExistingSelected: (passengerId: string) => void;
	onNotFound: (documentType: PassengerDocumentEnum, documentNumber: string) => void;
}

export const PassengerDocumentLookup = ({ onExistingSelected, onNotFound }: Props): JSX.Element => {
	const [documentType, setDocumentType] = useState<PassengerDocumentEnum>(PassengerDocumentEnum.CEDULA);
	const [documentNumber, setDocumentNumber] = useState('');
	const { loading, error, matches, searchByDocument, reset } = usePassengerByDocument();

	const handleSearch = async () => {
		const results = await searchByDocument(documentNumber, documentType);
		if (results.length === 0) {
			onNotFound(documentType, documentNumber.trim());
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			void handleSearch();
		}
	};

	return (
		<Box>
			<Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="flex-start">
				<FormControl size="small" sx={{ minWidth: 180 }}>
					<InputLabel>Tipo de documento</InputLabel>
					<Select
						label="Tipo de documento"
						value={documentType}
						onChange={(e) => {
							setDocumentType(e.target.value as PassengerDocumentEnum);
							reset();
						}}
					>
						{passengerDocumentType.map((opt) => (
							<MenuItem key={opt.id} value={opt.id}>
								{opt.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField
					size="small"
					fullWidth
					label="Número de documento"
					value={documentNumber}
					onChange={(e) => {
						setDocumentNumber(e.target.value);
						reset();
					}}
					onKeyDown={handleKeyDown}
					disabled={loading}
				/>
				<Button
					variant="contained"
					startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
					onClick={() => void handleSearch()}
					disabled={loading || documentNumber.trim().length < 4}
					sx={{ minWidth: 120, height: 40 }}
				>
					Buscar
				</Button>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}

			{matches.length > 0 && (
				<Box mt={2}>
					<Typography variant="body2" fontWeight={600} gutterBottom>
						Pasajeros encontrados
					</Typography>
					<List dense sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
						{matches.map((match: PassengerSearchMatch) => (
							<ListItemButton key={match.passengerId} onClick={() => onExistingSelected(match.passengerId)}>
								<ListItemText
									primary={`${match.name} ${match.lastName}`}
									secondary={match.documents.map((d) => `${d.documentType}: ${d.documentNumber}`).join(' · ')}
								/>
							</ListItemButton>
						))}
					</List>
					<Button size="small" sx={{ mt: 1 }} onClick={() => onNotFound(documentType, documentNumber.trim())}>
						Ninguno coincide — crear como nuevo
					</Button>
				</Box>
			)}
		</Box>
	);
};
